import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TravelerProfile } from '@marageti/z4-sdk/lib/people';
import { TripRequest, TripMatch, Review } from '@marageti/z4-sdk/lib/travel';
import { SearchResult, SearchRequest } from '@marageti/z4-sdk/lib/entity';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { useFetch, RichTextDisplay } from '@marageti/z4-lib';
import { Divider, Stack } from '@mui/material';
import { TravelerData } from './types';

// Helper components for traveler data sections
const TravelerTripRequestsSection = ({ travelerId, apiClient }: { travelerId: string; apiClient: any }) => {
  const { data: countryCodes } = useFetch<CountryCodeMap>(
    apiClient.standardsClient.getCountryCodes,
    [],
    true,
  );

  const searchRequest: SearchRequest = {
    filterBy: [{ field: 'travelers.id', value: [travelerId] }],
    sortBy: [{ field: 'departureDate', order: 'asc' }],
    pagination: { limit: 10, page: 1 },
  };
  const { data: tripRequests, isLoading } = useFetch<SearchResult<TripRequest>>(
    apiClient.tripRequestsClient.postTripRequestSearches,
    [searchRequest],
    !!travelerId,
  );

  if (isLoading) return <CircularProgress size={20} />;
  if (!tripRequests?.hits?.length) return <Typography color="text.secondary">No trip requests found</Typography>;

  const formatCountryCodesToString = (countryCodesArray: string[], countryCodeMap: { [key: string]: string }) => {
    let countryNames: string[] = [];
    if (countryCodesArray) {
      countryNames = countryCodesArray.map((c) => countryCodeMap[c]);
    }
    return countryNames.join(', ');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {tripRequests.hits.slice(0, 5).map((request: TripRequest) => (
        <Card key={request.id} variant="outlined" sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="bodyBold" sx={{ color: 'text.secondary' }}>
              {countryCodes ? formatCountryCodesToString(request.countries || [], countryCodes) : 'No countries specified'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <strong>Departure:</strong>
              {' '}
              {request.departureDate ? new Date(request.departureDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not specified'}
              {request.totalNights && (
              <>
                {' · '}
                <strong>
                  {request.totalNights}
                  {' '}
                  Nights
                </strong>
              </>
              )}
            </Typography>
            {request.returnDate && (
            <Typography variant="caption" color="text.secondary">
              <strong>Return:</strong>
              {' '}
              {new Date(request.returnDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              <strong>Travelers:</strong>
              {' '}
              {request.numberOfAdults || 0}
              {' '}
              Adults
              {request.numberOfChildren ? <span>{` · ${request.numberOfChildren} Children`}</span> : null}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <strong>Status:</strong>
              {' '}
              {request.status}
            </Typography>
            {request.createdAt && (
            <Typography variant="caption" color="text.secondary">
              <strong>Created:</strong>
              {' '}
              {new Date(request.createdAt).toLocaleDateString()}
            </Typography>
            )}
          </Box>
        </Card>
      ))}
      {tripRequests.hits.length > 5 && (
        <Typography variant="caption" color="text.secondary">
          +
          {tripRequests.hits.length - 5}
          {' '}
          more trip requests
        </Typography>
      )}
    </Box>
  );
};

const TravelerTripMatchesSection = ({ travelerId, apiClient }: { travelerId: string; apiClient: any }) => {
  const { data: countryCodes } = useFetch<CountryCodeMap>(
    apiClient.standardsClient.getCountryCodes,
    [],
    true,
  );

  const searchRequest: SearchRequest = {
    filterBy: [{ field: 'travelers.id', value: [travelerId] }],
    sortBy: [{ field: 'departureDate', order: 'asc' }],
    pagination: { limit: 10, page: 1 },
  };
  const { data: tripMatches, isLoading } = useFetch<SearchResult<TripMatch>>(
    apiClient.tripMatchesClient.postTripMatchSearches,
    [searchRequest],
    !!travelerId,
  );

  if (isLoading) return <CircularProgress size={20} />;
  if (!tripMatches?.hits?.length) return <Typography color="text.secondary">No trip matches found</Typography>;

  const formatCountryCodesToString = (countryCodesArray: string[], countryCodeMap: { [key: string]: string }) => {
    let countryNames: string[] = [];
    if (countryCodesArray) {
      countryNames = countryCodesArray.map((c) => countryCodeMap[c]);
    }
    return countryNames.join(', ');
  };

  // Group trip matches by trip request ID
  const groupedMatches = tripMatches.hits.reduce((groups: { [key: string]: TripMatch[] }, match) => {
    const requestId = match.tripRequestId || 'unknown';
    const newGroups = { ...groups };
    if (!newGroups[requestId]) {
      newGroups[requestId] = [];
    }
    newGroups[requestId].push(match);
    return newGroups;
  }, {});

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {Object.entries(groupedMatches).slice(0, 5).map(([requestId, matches]) => {
        const primaryMatch = matches[0];
        const isGrouped = matches.length > 1;

        return (
          <Box key={requestId} sx={{ display: 'flex', flexDirection: 'column' }}>
            {isGrouped ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {matches.map((match, index) => (
                  <Box key={match.id} sx={{ position: 'relative' }}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderLeft: index === 0 ? '3px solid #000000' : '1px solid #e0e0e0',
                        borderTop: index === 0 ? '1px solid #e0e0e0' : 'none',
                        borderRight: '1px solid #e0e0e0',
                        borderBottom: index === matches.length - 1 ? '1px solid #e0e0e0' : 'none',
                        borderRadius: (() => {
                          if (index === 0) return '8px 8px 0 0';
                          if (index === matches.length - 1) return '0 0 8px 8px';
                          return '0';
                        })(),
                        bgcolor: index === 0 ? 'grey.100' : 'background.paper',
                        '&:hover': {
                          bgcolor: index === 0 ? 'grey.200' : 'grey.50',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography variant="bodyBold" sx={{ color: 'text.secondary' }}>
                            {countryCodes ? formatCountryCodesToString(match.countries || [], countryCodes) : 'No countries specified'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor: index === 0 ? '#000000' : '#666666',
                                color: 'white',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                              }}
                            >
                              {index + 1}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Departure:</strong>
                          {' '}
                          {match.departureDate ? new Date(match.departureDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not specified'}
                          {match.totalNights && (
                            <>
                              {' · '}
                              <strong>
                                {match.totalNights}
                                {' '}
                                Nights
                              </strong>
                            </>
                          )}
                        </Typography>
                        {match.returnDate && (
                          <Typography variant="caption" color="text.secondary">
                            <strong>Return:</strong>
                            {' '}
                            {new Date(match.returnDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          <strong>Travelers:</strong>
                          {' '}
                          {match.numberOfAdults || 0}
                          {' '}
                          Adults
                          {match.numberOfChildren && match.numberOfChildren > 0 && (
                            <>
                              {' · '}
                              {match.numberOfChildren}
                              {' '}
                              Children
                            </>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Status:</strong>
                          {' '}
                          {match.status}
                          {match.partnerName && (
                            <>
                              {' · '}
                              <strong>Partner:</strong>
                              {' '}
                              {match.partnerName}
                            </>
                          )}
                        </Typography>
                        {match.createdAt && (
                          <Typography variant="caption" color="text.secondary">
                            <strong>Created:</strong>
                            {' '}
                            {new Date(match.createdAt).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    </Card>
                    {index < matches.length - 1 && (
                      <Box sx={{
                        height: 8,
                        width: 2,
                        background: 'linear-gradient(to bottom, #000000, #666666)',
                        mx: 'auto',
                        borderLeft: '1px solid #e0e0e0',
                        borderRight: '1px solid #e0e0e0',
                      }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Card variant="outlined" sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {countryCodes ? formatCountryCodesToString(primaryMatch.countries || [], countryCodes) : 'No countries specified'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Departure:</strong>
                    {' '}
                    {primaryMatch.departureDate ? new Date(primaryMatch.departureDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not specified'}
                    {primaryMatch.totalNights && (
                      <>
                        {' · '}
                        <strong>
                          {primaryMatch.totalNights}
                          {' '}
                          Nights
                        </strong>
                      </>
                    )}
                  </Typography>
                  {primaryMatch.returnDate && (
                    <Typography variant="caption" color="text.secondary">
                      <strong>Return:</strong>
                      {' '}
                      {new Date(primaryMatch.returnDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    <strong>Travelers:</strong>
                    {' '}
                    {primaryMatch.numberOfAdults || 0}
                    {' '}
                    Adults
                    {primaryMatch.numberOfChildren && primaryMatch.numberOfChildren > 0 && (
                      <>
                        {' · '}
                        {primaryMatch.numberOfChildren}
                        {' '}
                        Children
                      </>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Status:</strong>
                    {' '}
                    {primaryMatch.status}
                    {primaryMatch.partnerName && (
                      <>
                        {' · '}
                        <strong>Partner:</strong>
                        {' '}
                        {primaryMatch.partnerName}
                      </>
                    )}
                  </Typography>
                  {primaryMatch.createdAt && (
                    <Typography variant="caption" color="text.secondary">
                      <strong>Created:</strong>
                      {' '}
                      {new Date(primaryMatch.createdAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </Card>
            )}
          </Box>
        );
      })}
      {Object.keys(groupedMatches).length > 5 && (
        <Typography variant="caption" color="text.secondary">
          +
          {Object.keys(groupedMatches).length - 5}
          {' '}
          more trip match groups
        </Typography>
      )}
    </Box>
  );
};

const TravelerReviewsSection = ({ travelerId, apiClient }: { travelerId: string; apiClient: any }) => {
  const searchRequest: SearchRequest = {
    filterBy: [{ field: 'travelerId', value: [travelerId] }],
    pagination: { limit: 10, page: 1 },
  };
  const { data: reviews, isLoading } = useFetch<SearchResult<Review>>(
    apiClient.reviewsClient.postReviewsSearch,
    [searchRequest],
    !!travelerId,
  );

  if (isLoading) return <CircularProgress size={20} />;
  if (!reviews?.hits?.length) return <Typography color="text.secondary">No reviews found</Typography>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {reviews.hits.slice(0, 5).map((review) => (
        <Card key={review.id} variant="outlined" sx={{ p: 1 }}>
          <Typography variant="body2">
            <strong>Rating:</strong>
            {' '}
            {review.rating}
            /5
          </Typography>
          <Typography variant="body2">
            <strong>Created:</strong>
            {' '}
            {new Date(review.createdAt).toLocaleDateString()}
          </Typography>
        </Card>
      ))}
      {reviews.hits.length > 5 && (
        <Typography variant="caption" color="text.secondary">
          +
          {reviews.hits.length - 5}
          {' '}
          more reviews
        </Typography>
      )}
    </Box>
  );
};

const TravelerNotesSection = ({ travelerId, apiClient }: { travelerId: string; apiClient: any }) => {
  const { data: travelerProfile, isLoading } = useFetch<TravelerProfile>(
    apiClient.peopleClient.getTravelerProfile,
    [travelerId],
    !!travelerId,
  );

  if (isLoading) return <CircularProgress size={20} />;
  if (!travelerProfile?.travelerNotes?.length) return <Typography color="text.secondary">No notes found</Typography>;

  return (
    <Stack divider={<Divider />} spacing={2}>
      {travelerProfile.travelerNotes?.map((note) => (
        <Box key={note.id}>
          <RichTextDisplay key={note.id}>
            {note.content}
          </RichTextDisplay>
        </Box>
      ))}
    </Stack>
  );
};

interface TravelerCardContentProps {
  // Traveler data
  travelerData: TravelerData;
  // Box configuration
  placeholderText: string;

  // API client
  apiClient: any;

  // Accordion state
  expandedSections: {
    tripRequests: boolean;
    tripMatches: boolean;
    reviews: boolean;
    notes: boolean;
  };
  onAccordionChange: (section: string | number | symbol) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
}

const TravelerCardContent = ({
  travelerData,
  placeholderText,
  apiClient,
  expandedSections,
  onAccordionChange,
}: TravelerCardContentProps) => {
  const {
    traveler,
    loading,
    error,
    tripRequests,
    tripMatches,
    reviews,
    travelerProfile,
  } = travelerData;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>Traveler not found</Alert>;
  }

  if (traveler) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography>
            <strong>Email:</strong>
            {' '}
            {traveler.email}
          </Typography>
          <Typography>
            <strong>Name:</strong>
            {' '}
            {traveler.givenName}
            {' '}
            {traveler.familyName}
          </Typography>
          <Typography>
            <strong>Phone:</strong>
            {' '}
            {traveler.phone?.prefix}
            {traveler.phone?.number}
          </Typography>
          <Typography>
            <strong>Country:</strong>
            {' '}
            {traveler.countryOfResidence}
          </Typography>
          <Typography>
            <strong>Time Zone:</strong>
            {' '}
            {traveler.timeZone?.name}
          </Typography>
          <Typography>
            <strong>Tags:</strong>
            {' '}
            {traveler.tags?.join(', ') || 'None'}
          </Typography>
        </Box>

        <Accordion expanded={expandedSections.tripRequests} onChange={onAccordionChange('tripRequests')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontSize: '0.9rem' }}>
              Trip Requests
              {tripRequests?.hits?.length ? ` (${tripRequests.hits.length})` : ' (0)'}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TravelerTripRequestsSection travelerId={traveler.id} apiClient={apiClient} />
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expandedSections.tripMatches} onChange={onAccordionChange('tripMatches')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontSize: '0.9rem' }}>
              Trip Matches
              {tripMatches?.hits?.length ? ` (${tripMatches.hits.length})` : ' (0)'}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TravelerTripMatchesSection travelerId={traveler.id} apiClient={apiClient} />
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expandedSections.reviews} onChange={onAccordionChange('reviews')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontSize: '0.9rem' }}>
              Reviews
              {reviews?.hits?.length ? ` (${reviews.hits.length})` : ' (0)'}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TravelerReviewsSection travelerId={traveler.id} apiClient={apiClient} />
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expandedSections.notes} onChange={onAccordionChange('notes')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontSize: '0.9rem' }}>
              Notes
              {`(${travelerProfile?.travelerNotes?.length ?? 0})`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TravelerNotesSection travelerId={traveler.id} apiClient={apiClient} />
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }

  return (

    <Typography
      color="text.secondary"
      textAlign="center"
      justifyContent="center"
      sx={{ px: '10%', pt: 2 }}
    >
      {placeholderText}
    </Typography>
  );
};

interface TravelerCardProps {
  travelerData: TravelerData;
  title: string;
  placeholderText: string;
  apiClient: any;
  expandedSections: {
    tripRequests: boolean;
    tripMatches: boolean;
    reviews: boolean;
    notes: boolean;
  };
  onAccordionChange: (section: string | number | symbol) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
}

const TravelerCard = ({
  travelerData,
  title,
  placeholderText,
  apiClient,
  expandedSections,
  onAccordionChange,
}: TravelerCardProps) => {
  const { traveler } = travelerData;

  return (
    <Card sx={{ minHeight: 400 }}>
      <CardHeader title={traveler ? `${traveler.givenName} ${traveler.familyName}` : title} />
      <CardContent>
        <TravelerCardContent
          travelerData={travelerData}
          placeholderText={placeholderText}
          apiClient={apiClient}
          expandedSections={expandedSections}
          onAccordionChange={onAccordionChange}
        />
      </CardContent>
    </Card>
  );
};

export default TravelerCard;
